import type { NormalizedCacheObject } from "@apollo/client/index.js"
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink
} from "@apollo/client/index.js"
import { onError } from "@apollo/client/link/error"
import * as Sentry from "@sentry/remix"
import type { FC, ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import { useConfigContext } from "~/providers/ConfigProvider"
import { addShopifyCartBreadcrumb } from "~/helpers/sentry"
import { SHOPIFY_STOREFRONT_API_VERSION } from "~/shopify/apiVersion"

const ShopifyClientContext = createContext<
  undefined | ApolloClient<NormalizedCacheObject>
>(undefined)

export const ShopifyClientProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const config = useConfigContext()

  const client = useMemo(() => {
    const cache = new InMemoryCache()

    const httpLink = new HttpLink({
      uri: `https://${config.shopifyDomain}/api/${SHOPIFY_STOREFRONT_API_VERSION}/graphql.json`,
      headers: {
        "X-Shopify-Storefront-Access-Token": config.shopifyToken
      }
    })

    // Error link to capture all Apollo Client errors and send to Sentry
    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const operationName = operation.operationName
      const variables = operation.variables

      // Add breadcrumb for the error
      addShopifyCartBreadcrumb(
        `Apollo error in ${operationName}`,
        {
          operationName,
          variables,
          hasGraphQLErrors: !!graphQLErrors,
          hasNetworkError: !!networkError
        },
        "error"
      )

      // Capture GraphQL errors
      if (graphQLErrors) {
        graphQLErrors.forEach(error => {
          Sentry.withScope(scope => {
            scope.setTag("error_type", "graphql")
            scope.setTag("operation", operationName)
            scope.setContext("graphql_error", {
              message: error.message,
              path: error.path,
              extensions: error.extensions
            })
            scope.setContext("operation", {
              operationName,
              variables
            })
            Sentry.captureException(error)
          })
        })
      }

      // Capture network errors
      if (networkError) {
        Sentry.withScope(scope => {
          scope.setTag("error_type", "network")
          scope.setTag("operation", operationName)
          scope.setContext("operation", {
            operationName,
            variables
          })
          Sentry.captureException(networkError)
        })
      }
    })

    return new ApolloClient({
      link: ApolloLink.from([errorLink, httpLink]),
      cache,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
          nextFetchPolicy: "cache-and-network",
          notifyOnNetworkStatusChange: true
        }
      }
    })
  }, [config])

  return (
    <ShopifyClientContext.Provider value={client}>
      {children}
    </ShopifyClientContext.Provider>
  )
}

export const useShopifyClient = () => {
  const value = useContext(ShopifyClientContext)

  if (!value) {
    throw new Error("ShopifyClientContext is missing.")
  }

  return value
}

// Simple mock provider for Storybook
export const StorybookShopifyClientProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const client = useMemo(() => {
    return new ApolloClient({
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { fetchPolicy: "cache-only" },
        query: { fetchPolicy: "cache-only" }
      }
    })
  }, [])

  return (
    <ShopifyClientContext.Provider value={client}>
      {children}
    </ShopifyClientContext.Provider>
  )
}
